import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import { clearAuth, setAuth, setInitialized } from "../slices/authSlice";
import { useLoginMutation, useRegisterMutation, useVerifyMutation } from "../slices/authApi";
import { AuthLoginRequest } from "../interfaces/AuthLoginRequest";
import { AuthRegisterRequest } from "../interfaces/AuthRegisterRequest";
import LoadingView from "../views/LoadingView";

interface AuthContextType {
  username?: string;
  token?: string;
  error?: string;
  initialized: boolean;
  login: (values: AuthLoginRequest, callback: VoidFunction) => Promise<void>;
  logout: (callback: VoidFunction) => void;
  register: (values: AuthRegisterRequest, callback: VoidFunction) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextType>(null!);

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loginApi] = useLoginMutation();
  const [registerApi] = useRegisterMutation();
  const [verifyApi] = useVerifyMutation();
  const dispatch = useDispatch();
  const { username, token, error, initialized } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const initialize = async () => {
      const storedUsername = localStorage.getItem("username");
      const storedToken = localStorage.getItem("token");

      if (storedUsername && storedToken) {
        // Set token in Redux store before making the verify call
        dispatch(setAuth({ username: storedUsername, token: storedToken }));
        try {
          await verifyApi({ username: storedUsername }).unwrap();
        } catch (err) {
          dispatch(clearAuth()); // Clear invalid token
          localStorage.removeItem("username");
          localStorage.removeItem("token");
        }
      }
      dispatch(setInitialized());
    };

    if (!initialized) {
      initialize();
    }
  }, [dispatch, verifyApi, initialized]);

  const login = async (values: AuthLoginRequest, callback: VoidFunction) => {
    try {
      const { username, token } = await loginApi(values).unwrap(); // State updates via extraReducers
      localStorage.setItem("username", username);
      localStorage.setItem("token", token);
      callback();
    } catch (error) {
      // Error is automatically handled by Redux extraReducers - don't set error state here
    }
  };

  const logout = (callback: VoidFunction) => {
    dispatch(clearAuth());
    localStorage.removeItem("username");
    localStorage.removeItem("token");
    callback();
  };

  const register = async (values: AuthRegisterRequest, callback: VoidFunction) => {
    try {
      const { username, token } = await registerApi(values).unwrap(); // State updates via extraReducers
      localStorage.setItem("username", username);
      localStorage.setItem("token", token);
      callback();
    } catch (error) {
      // Error is automatically handled by Redux extraReducers - don't set error state here
    }
  };

  const value: AuthContextType = { username, token, error, initialized, login, logout, register };

  return initialized ? (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  ) : (
    <LoadingView />
  );
}
